import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import { fetchBlob } from "@/features/config/api";
import type {
  MikrotikSnippetResponse,
  OvpnCredencial,
  OvpnCredencialCreateInput,
  OvpnCredencialUpdateInput,
  OvpnCredencialesGeneradasResponse,
  OvpnDashboardStats,
  OvpnEstadoFilter,
  PagedOvpnCredenciales,
} from "./schemas";

const KEY = "ovpn-credenciales";
const KEY_DASH = "ovpn-dashboard";

export type OvpnFilters = {
  page?: number;
  size?: number;
  estado?: OvpnEstadoFilter | null;
  sin_router?: boolean | null;
  activo?: boolean | null;
  search?: string;
};

export function useOvpnDashboard(opts?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: [KEY_DASH],
    queryFn: () => apiCall<OvpnDashboardStats>("get", "/api/v1/ovpn/dashboard"),
    refetchInterval: opts?.refetchInterval ?? 30_000,
  });
}

export function useOvpnCredenciales(filters: OvpnFilters, opts?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () => {
      const p: Record<string, string | number | boolean> = {
        page: filters.page ?? 1,
        size: filters.size ?? 50,
      };
      if (filters.estado) p.estado = filters.estado;
      if (filters.sin_router !== undefined && filters.sin_router !== null) p.sin_router = filters.sin_router;
      if (filters.activo !== undefined && filters.activo !== null) p.activo = filters.activo;
      if (filters.search) p.search = filters.search;
      return apiCall<PagedOvpnCredenciales>(
        "get", "/api/v1/ovpn/credenciales", { searchParams: p },
      );
    },
    placeholderData: (prev) => prev,
    refetchInterval: opts?.refetchInterval ?? 30_000,
  });
}

export function useOvpnCredencial(id: string | null) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => apiCall<OvpnCredencial>("get", `/api/v1/ovpn/credenciales/${id}`),
    enabled: !!id,
  });
}

export function useCrearCredencial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: OvpnCredencialCreateInput) =>
      apiCall<OvpnCredencialesGeneradasResponse>(
        "post", "/api/v1/ovpn/credenciales", { json: data },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: [KEY_DASH] });
    },
  });
}

export function useEditarCredencial(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: OvpnCredencialUpdateInput) =>
      apiCall<OvpnCredencial>(
        "patch", `/api/v1/ovpn/credenciales/${id}`, { json: data },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: [KEY_DASH] });
    },
  });
}

export function useRegenerarPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiCall<OvpnCredencialesGeneradasResponse>(
        "post", `/api/v1/ovpn/credenciales/${id}/regenerate`, { json: {} },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useBorrarCredencial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiCall<{ message: string }>("delete", `/api/v1/ovpn/credenciales/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: [KEY_DASH] });
    },
  });
}

export function useMikrotikSnippet(id: string | null) {
  return useQuery({
    queryKey: [KEY, "snippet", id],
    queryFn: () =>
      apiCall<MikrotikSnippetResponse>(
        "get", `/api/v1/ovpn/credenciales/${id}/snippet-mikrotik`,
      ),
    enabled: !!id,
  });
}

/** Descarga el archivo .ovpn (binary-safe via fetch nativo). */
export async function descargarOvpnConfig(credencialId: string, filename: string) {
  const blob = await fetchBlob(`/api/v1/ovpn/credenciales/${credencialId}/ovpn-config`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

